import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiX, FiAlertTriangle, FiTrash2, FiLoader } from 'react-icons/fi';

interface DeleteTaskModalProps {
  task: any | null;
  onConfirm: () => void;
  onClose: () => void;
  isDeleting?: boolean;
}

export const DeleteTaskModal: React.FC<DeleteTaskModalProps> = ({
  task,
  onConfirm,
  onClose,
  isDeleting = false,
}) => {
  return (
    <AnimatePresence>
      {task && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-md bg-[#0B0B0B] border border-white/10 rounded-[28px] p-5 sm:p-6 text-left flex flex-col max-h-[82vh] z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)] overflow-hidden"
          >
            {/* Header */}
            <div className="shrink-0 flex items-start justify-between pb-4 mb-4 border-b border-white/5 bg-[#0B0B0B] z-20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 flex items-center justify-center shrink-0">
                  <FiAlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Delete Task</h2>
                  <p className="text-zinc-400 text-xs mt-0.5">This action cannot be undone</p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto no-scrollbar space-y-4 pr-1">
              <p className="text-zinc-300 text-xs sm:text-sm leading-relaxed">
                Are you sure you want to delete this task?
              </p>
              <div className="w-full bg-[#121212] border border-white/5 rounded-2xl px-4 py-3.5 text-white text-xs font-bold truncate">
                {task.title}
              </div>
            </div>

            {/* Footer */}
            <div className="shrink-0 flex items-center justify-end gap-3 pt-4 mt-4 border-t border-white/5 bg-[#0B0B0B] z-20">
              <button
                type="button"
                onClick={onClose}
                disabled={isDeleting}
                className="px-5 py-2.5 rounded-full border border-white/10 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 text-xs font-bold transition-all cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={onConfirm}
                className="px-5 py-2.5 rounded-full bg-rose-600 hover:bg-rose-500 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-lg shadow-rose-600/20"
              >
                {isDeleting ? (
                  <>
                    <FiLoader className="w-3.5 h-3.5 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <FiTrash2 className="w-3.5 h-3.5" />
                    <span>Delete Task</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
